import type Phaser from 'phaser';
import type { SecretYard } from '../../gen/secretYards';
import { TILE_SIZE } from '../layout';
import { gameAudio } from '../audio/audio';
import { haptics } from '../../platform/haptics';
import { label, UI } from '../ui/text';
import { reducedMotion } from '../ui/motion';
import { TILES_KEY } from './placeholderArt';
import { PROP_LIFT, propFrame, propFor, PROPS_KEY } from './props';
import { frameIndex } from './tileFrames';

/**
 * A secret yard on the lawn's view (owner, 2026-10-05): the gate standing in a gap in the fence
 * (a gold glint behind it now and then), and the yard beyond, kept hidden (the surroundings show
 * there) until the gate gives way. Then it appears: a hedge-ringed garden in fresh stripes round a
 * fountain, a stone pedestal at the far end, sparkles, a chime and SECRET YARD!.
 */
export class YardView {
  private readonly garden: Phaser.GameObjects.Container;
  private gateSprite: Phaser.GameObjects.Image | null;
  private glint: Phaser.Time.TimerEvent | null = null;
  opened = false;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly yard: SecretYard,
    gateDepth: number,
  ) {
    const T = TILE_SIZE;
    const { gate, area } = yard;
    // The gate item, standing in the gap.
    const prop = propFor(yard.item);
    this.gateSprite = prop
      ? scene.add
          .image((gate.x + 0.5) * T, (gate.y + 1) * T - PROP_LIFT, PROPS_KEY, propFrame(prop))
          .setOrigin(0.5, 1)
          .setDepth(gateDepth)
      : scene.add
          .image((gate.x + 0.5) * T, (gate.y + 0.5) * T, TILES_KEY, frameIndex('sticks'))
          .setDepth(gateDepth);
    // Now and then a glint of gold from beyond the gap: something is back there.
    if (!reducedMotion()) {
      this.glint = scene.time.addEvent({
        delay: 3800,
        loop: true,
        callback: () => this.sparkle((gate.x + 0.5) * T, (gate.y + 0.5) * T, 1),
      });
    }

    // The garden, hidden until the gate opens: a ring of hedge, the yard in fresh stripes, the
    // fountain, a pedestal.
    const parts: Phaser.GameObjects.GameObject[] = [];
    const tile = (x: number, y: number, frame: string) =>
      parts.push(
        scene.add.image((x + 0.5) * T, (y + 0.5) * T, TILES_KEY, frameIndex(frame as never)),
      );
    const across = yard.side === 'top' || yard.side === 'bottom';
    for (let y = area.y - 1; y <= area.y + area.h; y++) {
      for (let x = area.x - 1; x <= area.x + area.w; x++) {
        const inside = x >= area.x && x < area.x + area.w && y >= area.y && y < area.y + area.h;
        if (x === gate.x && y === gate.y) {
          tile(x, y, across ? 'mowedN' : 'mowedE');
          continue;
        }
        if (!inside) {
          tile(x, y, 'hedge');
          continue;
        }
        // Stripes running away from the gate, alternating.
        const stripe = across
          ? x % 2 === 0
            ? 'mowedN'
            : 'mowedS'
          : y % 2 === 0
            ? 'mowedE'
            : 'mowedW';
        tile(x, y, stripe);
      }
    }
    tile(yard.fountain.x, yard.fountain.y, 'fountain');
    const plinth = scene.add.graphics();
    const px = yard.pedestal.x * T;
    const py = yard.pedestal.y * T;
    plinth.fillStyle(0x10180c).fillRect(px + 2, py + 9, 13, 6);
    plinth.fillStyle(0xb8b2a8).fillRect(px + 2, py + 8, 12, 6);
    plinth.fillStyle(0xe2ddd2).fillRect(px + 2, py + 8, 12, 2);
    parts.push(plinth);
    this.garden = scene.add.container(0, 0, parts).setDepth(-10).setVisible(false);
  }

  /** Whether (x, y) lies in the yard (its pickups stay hidden until it opens). */
  contains(x: number, y: number): boolean {
    const { area } = this.yard;
    return x >= area.x && x < area.x + area.w && y >= area.y && y < area.y + area.h;
  }

  /** The gate gave way: the yard appears with a fanfare. */
  open(): void {
    if (this.opened) return;
    this.opened = true;
    this.glint?.remove();
    const T = TILE_SIZE;
    const { gate, area } = this.yard;
    if (this.gateSprite) {
      const g = this.gateSprite;
      this.gateSprite = null;
      this.scene.tweens.add({
        targets: g,
        alpha: 0,
        scaleY: 0.3,
        duration: 220,
        onComplete: () => g.destroy(),
      });
    }
    this.garden.setVisible(true).setAlpha(reducedMotion() ? 1 : 0);
    // In full view within the scene's hold (LevelScene YARD_REVEAL_HOLD_MS), before the mower moves.
    if (!reducedMotion()) this.scene.tweens.add({ targets: this.garden, alpha: 1, duration: 650 });
    gameAudio.chime(true);
    haptics.impact();
    // Sparkles over the yard, and the news.
    const cx = (area.x + area.w / 2) * T;
    const cy = (area.y + area.h / 2) * T;
    for (let i = 0; i < 4; i++) {
      this.scene.time.delayedCall(i * 160, () =>
        this.sparkle(
          cx + (Math.random() - 0.5) * area.w * T,
          cy + (Math.random() - 0.5) * area.h * T,
          8,
        ),
      );
    }
    const banner = label(
      this.scene,
      (gate.x + 0.5) * T,
      gate.y * T - 10,
      'SECRET YARD!',
      1,
      UI.accent,
    )
      .setOrigin(0.5, 1)
      .setDepth(60);
    this.scene.tweens.add({
      targets: banner,
      y: banner.y - 14,
      alpha: { from: 1, to: 0 },
      delay: 900,
      duration: 900,
      onComplete: () => banner.destroy(),
    });
  }

  /** A few gold and white sparks at (x, y). */
  private sparkle(x: number, y: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const s = this.scene.add.graphics({ x, y }).setDepth(55);
      s.fillStyle(i % 2 ? 0xffffff : UI.accent)
        .fillRect(-1, 0, 3, 1)
        .fillRect(0, -1, 1, 3);
      const a = Math.random() * Math.PI * 2;
      this.scene.tweens.add({
        targets: s,
        x: x + Math.cos(a) * (6 + Math.random() * 10),
        y: y + Math.sin(a) * (6 + Math.random() * 10) - 4,
        alpha: 0,
        scale: { from: 1.4, to: 0.4 },
        duration: 600 + Math.random() * 300,
        onComplete: () => s.destroy(),
      });
    }
  }
}
